import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search } from "lucide-react";
import { JSX } from "react";
import {
  IManagerCandidate,
  IManualIdentityPreview,
} from "@/@types/divergence";

import {
  CPF_DIGITS,
  EmploymentNotice,
  employmentNoticeOf,
  onlyDigits,
} from "../../utils/manual-identity";
import { MANUAL_IDENTITY_LABELS } from "../../utils/messages";
import { EmployeeRegistryPanel } from "./employee-registry-panel";
import { EmploymentNoticeBanner } from "./employment-notice-banner";
import { MANUAL_IDENTITY_TEST_ID } from "../../utils/decision-test-ids";

export const MANUAL_IDENTITY_CANDIDATES_TEST_ID: string =
  "manual-identity-candidates";

export const MANUAL_IDENTITY_HINT_TEST_ID: string = "manual-identity-hint";

export const MANUAL_IDENTITY_KNOWN_TEST_ID: string = "manual-identity-known";

interface ManualIdentityFallbackProps {
  blockMessage: string;
  candidates: IManagerCandidate[];

  knownOperator?: IManagerCandidate | null;
  preview: IManualIdentityPreview | null;
  isSearching: boolean;
  searchError: string | null;
  cpf: string;
  chosen: string;
  acknowledgedFor: string;
  candidatesFailed?: boolean;
  onCpfChange: (value: string) => void;
  onChosenChange: (value: string) => void;
  onAcknowledgeChange: (value: string) => void;
  onSearch: (cpf: string) => void;
}

export function ManualIdentityFallback({
  blockMessage,
  candidates,
  knownOperator,
  preview,
  isSearching,
  searchError,
  cpf,
  chosen,
  acknowledgedFor,
  candidatesFailed,
  onCpfChange,
  onChosenChange,
  onAcknowledgeChange,
  onSearch,
}: ManualIdentityFallbackProps): JSX.Element {
  const byCandidate: boolean = candidates.length > 0;

  const byCpf: boolean = !byCandidate || Boolean(candidatesFailed);

  const digits: string = onlyDigits(cpf);
  const canSearch: boolean = digits.length === CPF_DIGITS && !isSearching;
  const notice: EmploymentNotice | null = preview
    ? employmentNoticeOf(preview.employment)
    : null;

  const acknowledged: boolean =
    Boolean(preview) && acknowledgedFor === preview?.identity.cpf;

  return (
    <div
      data-testid={MANUAL_IDENTITY_TEST_ID}
      className="flex w-full flex-col gap-4"
    >
      {Boolean(blockMessage) && (
        <p className="text-sm text-muted-foreground">{blockMessage}</p>
      )}

      {byCandidate && (
        <div
          data-testid={MANUAL_IDENTITY_CANDIDATES_TEST_ID}
          className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3"
        >
          <span className="text-xs font-medium uppercase tracking-wide text-amber-900">
            {MANUAL_IDENTITY_LABELS.CANDIDATES_TITLE}
          </span>
          {candidates.map((candidate: IManagerCandidate) => (
            <label
              key={candidate.operatorCode}
              className="flex cursor-pointer items-center gap-3 rounded-md border border-transparent bg-white/60 px-2 py-1.5 text-sm text-amber-900 hover:border-amber-400"
            >
              <input
                type="radio"
                name="manual-identity-candidate"
                value={candidate.operatorCode}
                checked={chosen === candidate.operatorCode}
                disabled={isSearching}
                onChange={(): void => onChosenChange(candidate.operatorCode)}
              />
              <span className="flex items-baseline gap-1.5">
                <span className="text-xs uppercase tracking-wide text-amber-700">
                  {MANUAL_IDENTITY_LABELS.CANDIDATE_OPERATOR}
                </span>
                <span className="font-mono font-medium">
                  {candidate.operatorCode}
                </span>
              </span>
              <span className="flex flex-1 items-baseline justify-end gap-1.5">
                <span className="text-xs uppercase tracking-wide text-amber-700">
                  {MANUAL_IDENTITY_LABELS.CANDIDATE_NAME}
                </span>
                <span className="font-medium">
                  {candidate.name || MANUAL_IDENTITY_LABELS.EMPTY_FIELD}
                </span>
              </span>
            </label>
          ))}
          <span className="text-xs text-amber-800">
            {MANUAL_IDENTITY_LABELS.CANDIDATES_HINT}
          </span>
          {isSearching && (
            <span className="text-xs text-amber-800">
              {MANUAL_IDENTITY_LABELS.SEARCHING}
            </span>
          )}
        </div>
      )}

      {knownOperator && !byCandidate && (
        <p
          data-testid={MANUAL_IDENTITY_KNOWN_TEST_ID}
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          {MANUAL_IDENTITY_LABELS.KNOWN_OPERATOR(
            knownOperator.name,
            knownOperator.operatorCode,
          )}
        </p>
      )}

      {byCpf && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="manual-identity-cpf">
            {MANUAL_IDENTITY_LABELS.CPF_LABEL}
          </Label>
          <div className="flex gap-2">
            <Input
              id="manual-identity-cpf"
              inputMode="numeric"
              maxLength={CPF_DIGITS}
              value={cpf}
              placeholder={MANUAL_IDENTITY_LABELS.CPF_PLACEHOLDER}
              onKeyDown={(event): void => {
                if (event.key === "Enter" && canSearch) onSearch(digits);
              }}
              onChange={(event): void =>
                onCpfChange(onlyDigits(event.target.value))
              }
            />
            <Button
              type="button"
              variant="ghost"
              disabled={!canSearch}
              onClick={(): void => onSearch(digits)}
            >
              <Search className="w-4 h-4 mr-1" aria-hidden="true" />
              {isSearching
                ? MANUAL_IDENTITY_LABELS.SEARCHING
                : MANUAL_IDENTITY_LABELS.SEARCH}
            </Button>
          </div>
          {Boolean(searchError) && (
            <p role="alert" className="text-sm text-destructive">
              {searchError}
            </p>
          )}
        </div>
      )}

      {preview && <EmployeeRegistryPanel preview={preview} />}

      {notice && (
        <EmploymentNoticeBanner
          notice={notice}
          acknowledged={acknowledged}
          onAcknowledge={(value: boolean): void =>
            onAcknowledgeChange(value ? (preview?.identity.cpf ?? "") : "")
          }
        />
      )}

      {preview && (
        <p className="text-sm text-muted-foreground">
          {MANUAL_IDENTITY_LABELS.RESPONSIBILITY}
        </p>
      )}
    </div>
  );
}
